import { rgbDataURL } from "@/utils/tools";
import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";
import { FaPause, FaPlay } from "react-icons/fa";
import ReactPlayer from "react-player";
import { MdExplicit } from "react-icons/md";
import millify from "millify";
import useAuthModal from "@/hooks/useAuthModal";
import { FaSpotify } from "react-icons/fa6";
import { RiVerifiedBadgeFill } from "react-icons/ri";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

/**
 * Calculate optimal preview start and end times based on track duration
 * @param {number} duration - Total duration of the track in seconds
 * @param {number} previewLength - Desired length of preview in seconds (default: 15)
 * @param {number} preferredStartTime - Preferred start time of the preview in seconds
 * @returns {Object} - { startTime, endTime }
 */
const calculatePreviewTimes = (
  duration,
  previewLength = 15,
  preferredStartTime,
) => {
  // Use preferred start time if available and valid
  if (typeof preferredStartTime === "number" && preferredStartTime >= 0) {
    const startTime = preferredStartTime;
    const endTime = Math.min(duration, startTime + previewLength);
    return {
      startTime: Math.round(startTime),
      endTime: Math.round(endTime),
    };
  }

  if (!duration || duration <= 0) {
    return { startTime: 0, endTime: previewLength };
  }

  if (duration <= previewLength * 2) {
    return { startTime: 0, endTime: Math.min(duration, previewLength) };
  }

  const targetPosition = duration * 0.4;

  let startTime = Math.max(0, targetPosition - previewLength * 0.3);
  let endTime = startTime + previewLength;

  if (endTime > duration) {
    endTime = duration;
    startTime = Math.max(0, endTime - previewLength);
  }

  return {
    startTime: Math.round(startTime),
    endTime: Math.round(endTime),
  };
};

const Song = ({ song, mode, isPlaying, onPlayPause, onSelect, isClip }) => {
  const [progress, setProgressLocal] = useState(0);

  const playerRef = useRef(null);
  const [hasWindow, setHasWindow] = useState(false);
  const lastUpdateTimeRef = useRef(0);
  const { checkAuthAndExecute } = useAuthModal();
  const [loading, setLoading] = useState(false);

  // Calculate preview times based on song duration
  const { startTime, endTime } = calculatePreviewTimes(
    song?.duration,
    15,
    song?.metadata?.preferredStartTime,
  );
  const previewDuration = endTime - startTime;
  const baseUrl =
    song.urls.base || song.urls.vocals_clean || song.urls.instrumentals;
  const previewUrl = baseUrl
    ? `${baseUrl}#t=${startTime},${endTime}`
    : song.urls.preview;

  useEffect(() => {
    if (typeof window !== "undefined") {
      setHasWindow(true);
    }
  }, []);

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleProgress = () => {
    // Throttle updates
    if (
      isPlaying &&
      playerRef.current &&
      Date.now() - lastUpdateTimeRef.current > 250
    ) {
      // Calculate progress based on current time within the preview window
      const currentTime = playerRef.current?.getCurrentTime() || 0;
      // ReactPlayer might return time relative to the full track, so we need to adjust
      const adjustedTime = Math.max(
        0,
        Math.min(previewDuration, currentTime - startTime),
      );
      const progressPercentage =
        previewDuration > 0 ? (adjustedTime / previewDuration) * 100 : 0;

      setProgressLocal(progressPercentage);
      lastUpdateTimeRef.current = Date.now();
    }
  };

  const handlePlaybackEnd = () => {
    onPlayPause();
    setProgressLocal(0);
    playerRef.current?.seekTo(0);
  };

  const handlePlayClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onPlayPause();
  };
  dayjs.extend(relativeTime);

  return (
    <div
      onClick={() => {
        if (!checkAuthAndExecute()) return false;

        setLoading(true);

        // Construct details object
        const songDetails = {
          url:
            song.urls?.base ||
            song.urls?.vocals_clean ||
            song.urls?.instrumentals,
          title: song.metadata?.title || song.title || "Untitled Song",
          metadata: song.metadata, // Pass along metadata
          _id: song._id,
        };

        // Basic validation
        if (!songDetails.url || !songDetails.title) {
          console.error("Missing URL or Title for selected song:", song);
          // Optionally show a user-facing error, e.g., using toast
          // toast.error("Could not select this song. Missing details.");
          setLoading(false);
          return;
        }

        // Simulate delay (optional, can be removed)
        setTimeout(() => {
          if (onSelect) {
            onSelect(songDetails); // Call the new callback
          }
          // Old prop calls removed:
          // setFinalUrl(...);
          // setProgress(100);
          // setLibrarySong(song);
          // if (nextStage) { nextStage(); }

          setLoading(false);
        }, Math.random() * 500); // Shorter delay example
      }}
      key={song?._id}
      className={`tw-flex ${mode === "dark" && "tw-bg-purple-700/40"} tw-justify-between ${mode === "dark" ? "hover:tw-bg-purple-700/60" : "hover:tw-bg-gray-50"} ${loading && "tw-opacity-50"} tw-items-center tw-gap-2 tw-transition-all tw-cursor-pointer tw-p-3 tw-rounded-2xl ${mode === "dark" ? "tw-border-0" : "tw-border"} tw-border-solid ${mode === "dark" ? "tw-border-white/40" : "tw-border-black/10"}`}
    >
      <div className="tw-flex tw-gap-2 tw-items-center tw-pr-0 tw-flex-1 tw-min-w-0">
        <div
          onClick={handlePlayClick}
          className="tw-relative tw-w-10 tw-min-w-10 tw-min-h-10 tw-h-10 tw-bg-purple-200 tw-rounded-full"
        >
          <svg
            className="tw-w-full tw-h-full tw-overflow-visible"
            viewBox="0 0 40 40"
          >
            <circle
              className="tw-text-purple-700"
              strokeWidth="3"
              stroke="currentColor"
              fill="transparent"
              r="19"
              cx="20"
              cy="20"
              strokeDasharray={2 * Math.PI * 19}
              strokeDashoffset={(2 * Math.PI * 19 * (100 - progress)) / 100}
              transform="rotate(-90 20 20)"
            />
          </svg>
          <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-text-sm tw-justify-center tw-rounded-full">
            {isPlaying ? (
              <FaPause className="tw-text-purple-700" />
            ) : (
              <FaPlay className="tw-text-purple-700 tw-ml-1" />
            )}
          </div>
        </div>

        <Image
          src={song?.metadata?.image?.url ?? song?.image?.url}
          alt="s"
          width={62}
          height={62}
          className="tw-rounded-xl"
          blurDataURL={rgbDataURL(
            song?.image?.blurhash.split(",")[0],
            song?.image?.blurhash.split(",")[1],
            song?.image?.blurhash.split(",")[2],
          )}
          placeholder="blur"
        />
        <div className="tw-flex tw-flex-col tw-justify-between tw-min-w-0">
          <div
            className={`tw-font-black tw-flex tw-items-center tw-gap-1 tw-w-full tw-text-sm tw-truncate ${mode === "dark" ? "tw-text-white/80" : "tw-text-black/50"}`}
          >
            {isClip ? null : song?.isLibrary ? (
              <RiVerifiedBadgeFill className="tw-text-purple-700" size={18} />
            ) : (
              <Image
                src={song?.user?.avatar}
                alt="user"
                width={18}
                height={18}
                className="tw-rounded-full"
              />
            )}
            <span className="tw-truncate">{song?.metadata?.artist}</span>
            {song.uses < 1000 && (
              <div className="tw-bg-green-400 tw-text-white tw-p-1 tw-px-1.5 tw-py-0.5 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-text-xs tw-font-black">
                NEW
              </div>
            )}
          </div>

          <div className="tw-font-black md:tw-text-lg tw-truncate">
            {song?.metadata?.title ?? song?.title}
          </div>
          <span
            className={`${mode === "dark" ? "tw-text-white/80" : "tw-text-black/50"} tw-text-sm tw-font-bold tw-flex tw-text-center tw-items-center`}
          >
            {song?.metadata?.streamingLinks?.find(
              (link) => link.platform === "spotify",
            ) && (
              <FaSpotify
                className="tw-text-[#1DB954] tw-cursor-pointer tw-mr-1"
                size={20}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  window.open(
                    song?.metadata?.streamingLinks?.find(
                      (link) => link.platform === "spotify",
                    )?.url,
                    "_blank",
                  );
                }}
              />
            )}
            {song?.metadata?.isExplicit && (
              <MdExplicit size={17} className="tw-mr-0.5" />
            )}

            <span style={{ marginTop: 1.5 }}>
              {millify(song?.uses)} Uses ·{" "}
              {formatDuration(Math.round(song?.duration))}
              {!song?.isLibrary && song.createdAt && (
                <span>· {dayjs(song.createdAt).fromNow()}</span>
              )}
            </span>
          </span>
        </div>
      </div>
      {hasWindow && isPlaying && (
        <ReactPlayer
          style={{
            display: "none",
          }}
          onProgress={handleProgress}
          ref={playerRef}
          url={previewUrl}
          playing={isPlaying}
          onEnded={handlePlaybackEnd}
        />
      )}
    </div>
  );
};

export default Song;
